import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  Descriptions,
  Table,
  Card,
  Spin,
  Button,
  Modal,
  Select,
  Input,
  message,
} from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import moment from 'moment';
import ReactEcharts from 'echarts-for-react';
import AdminLayout from '../../../components/admin/AdminLayout';
import { adminApi } from '../../../modules/api';
import { clearSession } from '../../../modules/auth';
import { formatMonthlyPrice } from '../../../utils/pricing';
import { displayPlanName } from '../../../utils/planDisplay';
import './adminCustomers.css';

class AdminCustomerDetail extends Component {
  state = {
    loading: true,
    detail: null,
    error: null,
    products: [],
    assignModalVisible: false,
    assignLoading: false,
    assignProductId: '',
    assignStartDate: '',
    requestModalVisible: false,
    requestLoading: false,
    requestProductId: '',
    editProfileVisible: false,
    editProfileLoading: false,
    profileFields: {
      email: '',
      name: '',
      company: '',
      phone: '',
      country: '',
    },
    usagePeriod: '30d',
  };

  componentDidMount() {
    this.fetchDetail();
    this.loadProducts();
  }

  componentDidUpdate(prevProps) {
    const { match } = this.props;
    if (match.params.userId !== prevProps.match.params.userId) {
      this.fetchDetail();
      this.loadProducts();
    }
  }

  async fetchDetail() {
    const { match } = this.props;
    this.setState({ loading: true, error: null });
    try {
      const response = await adminApi.fetchCustomerDetail(match.params.userId);
      const data = response && response.data ? response.data : response;
      this.setState({ detail: data, loading: false });
    } catch (error) {
      this.setState({
        loading: false,
        error: 'Unable to load customer details. Try again later.',
      });
    }
  }

  async loadProducts() {
    this.setState({ productsError: null });
    try {
      const response = await adminApi.fetchProducts();
      const data = response && response.data ? response.data : response;
      this.setState({ products: data || [] });
    } catch (error) {
      this.setState({ products: [] });
    }
  }

  openAssignModal = () => {
    this.setState({ assignModalVisible: true });
  };

  closeAssignModal = () => {
    this.setState({
      assignModalVisible: false,
      assignLoading: false,
      assignProductId: '',
      assignStartDate: '',
    });
  };

  openRequestModal = () => {
    this.setState({ requestModalVisible: true });
  };

  openEditProfile = () => {
    const { detail } = this.state;
    const user = detail && detail.user;
    if (!user) return;
    this.setState({
      editProfileVisible: true,
      profileFields: {
        email: user.email || '',
        name: user.name || '',
        company: user.company || '',
        phone: user.phone || '',
        country: user.country || '',
      },
    });
  };

  closeEditProfile = () => {
    this.setState({ editProfileVisible: false, editProfileLoading: false });
  };

  updateProfileField = (field, value) => {
    this.setState((state) => ({
      profileFields: { ...state.profileFields, [field]: value },
    }));
  };

  handleSaveProfile = async () => {
    const { match } = this.props;
    const { profileFields, detail } = this.state;
    const email = profileFields.email.trim().toLowerCase();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      message.error('Enter a valid email address.');
      return;
    }

    const payload = {
      email,
      name: profileFields.name.trim(),
      company: profileFields.company.trim(),
      phone: profileFields.phone.trim(),
      country: profileFields.country.trim(),
    };
    this.setState({ editProfileLoading: true });
    try {
      const response = await adminApi.updateCustomerProfile(match.params.userId, payload);
      const updated = response && response.data ? response.data : response;
      this.setState({
        detail: { ...detail, user: updated.user },
        editProfileVisible: false,
        editProfileLoading: false,
      });
      message.success('Customer profile updated.');
    } catch (error) {
      const errorMessage = error && error.response && error.response.data
        && error.response.data.message;
      message.error(errorMessage || 'Unable to update customer profile.');
      this.setState({ editProfileLoading: false });
    }
  };

  handleDelete = async () => {
    const { match, history } = this.props;
    try {
      await adminApi.deleteCustomer(match.params.userId);
      message.success('Customer account removed.');
      history.push('/admin/customers');
    } catch (error) {
      const errorMessage = error && error.response && error.response.data
        && error.response.data.message;
      message.error(errorMessage || 'Unable to remove this customer account.');
    }
  };

  closeRequestModal = () => {
    this.setState({
      requestModalVisible: false,
      requestLoading: false,
      requestProductId: '',
    });
  };

  formatProductPeriod = (product) => {
    if (!product) {
      return '';
    }
    const periodValue = Number(product.period) || 0;
    const unit = product.periodUnit || 'day';
    const suffix = periodValue === 1 ? '' : 's';
    return `${periodValue} ${unit}${suffix}`;
  };

  buildRequestPayload = (product) => {
    const productId = product && (product._id || product.id);
    const period = Number(product.period) || 1;
    const periodUnit = product.periodUnit || 'month';
    const price = Number(product.price || 0);
    return {
      productId,
      planId: productId,
      planName: product.description,
      planPriceAmount: price,
      planPriceDisplay: `$${price.toFixed(2)} / ${this.formatProductPeriod(product)}`,
      planBillingPeriod: period,
      planBillingUnit: periodUnit,
      planFeatures: Array.isArray(product.features) ? product.features : [],
    };
  };

  handleAssign = async () => {
    const { assignProductId, assignStartDate } = this.state;
    const { match } = this.props;
    if (!assignProductId) {
      message.warning('Select a plan before assigning.');
      return;
    }
    this.setState({ assignLoading: true });
    try {
      await adminApi.assignSubscription({
        userId: match.params.userId,
        productId: assignProductId,
        startsAt: assignStartDate || undefined,
      });
      message.success('Credit plan assigned.');
      this.closeAssignModal();
      this.fetchDetail();
    } catch (error) {
      const errMessage = (error && error.response && error.response.data && error.response.data.message)
        ? error.response.data.message
        : 'Unable to assign credit plan.';
      message.error(errMessage);
      this.setState({ assignLoading: false });
    }
  };

  handleCreatePaymentRequest = async () => {
    const { requestProductId, products } = this.state;
    const { match } = this.props;
    if (!requestProductId) {
      message.warning('Select a plan before creating a payment request.');
      return;
    }

    const product = products.find((item) => (item._id || item.id) === requestProductId);
    if (!product) {
      message.warning('Selected plan is no longer available.');
      return;
    }

    this.setState({ requestLoading: true });
    try {
      await adminApi.createSubscriptionRequest({
        userId: match.params.userId,
        ...this.buildRequestPayload(product),
      });
      message.success('Payment request created.');
      this.closeRequestModal();
      this.fetchDetail();
    } catch (error) {
      const errMessage = (error && error.response && error.response.data && error.response.data.message)
        ? error.response.data.message
        : 'Unable to create payment request.';
      message.error(errMessage);
      this.setState({ requestLoading: false });
    }
  };

  renderSubscriptionTable() {
    const { detail } = this.state;
    if (!detail || !detail.subscriptions || !detail.subscriptions.length) {
      return <p>No credit plans found.</p>;
    }

    const columns = [
      {
        title: 'Plan',
        dataIndex: ['product', 'description'],
        key: 'plan',
      },
      {
        title: 'Monthly price',
        dataIndex: ['product', 'price'],
        key: 'price',
        render: (value, record) => {
          const product = record.product || {};
          const priceAmount =
            value !== undefined && value !== null ? value : product.price;
          return formatMonthlyPrice(priceAmount, product.period, product.periodUnit);
        },
      },
      {
        title: 'Starts',
        dataIndex: 'startsAt',
        key: 'startsAt',
        render: (value) => (value ? new Date(value).toLocaleString() : '—'),
      },
      {
        title: 'Ends',
        dataIndex: 'endsAt',
        key: 'endsAt',
        render: (value) => (value ? new Date(value).toLocaleString() : '—'),
      },
      {
        title: 'Status',
        key: 'status',
        render: (value, record) => {
          const status = String(record.status || '').toLowerCase();
          if (status === 'active') {
            return 'Active';
          }
          if (status === 'queued') {
            return 'Queued';
          }
          if (status === 'expired') {
            return 'Expired';
          }
          return '—';
        },
      },
    ];

    return (
      <Table
        rowKey={(record) => record._id}
        columns={columns}
        dataSource={detail.subscriptions}
        pagination={false}
        size="small"
      />
    );
  }

  renderUsageTable() {
    const { detail } = this.state;
    if (!detail || !detail.usage || !detail.usage.length) {
      return <p>No API usage recorded.</p>;
    }

    const columns = [
      {
        title: 'Action',
        dataIndex: 'action',
        key: 'action',
      },
      {
        title: 'Success',
        dataIndex: 'success',
        key: 'success',
        render: (value) => (value ? 'Yes' : 'No'),
      },
      {
        title: 'Credits used',
        dataIndex: 'creditsUsed',
        key: 'creditsUsed',
        render: (value) => (value == null ? '—' : Number(value).toLocaleString()),
      },
      {
        title: 'Duration (ms)',
        dataIndex: 'durationMs',
        key: 'durationMs',
        render: (value) => (value == null ? '—' : value),
      },
      {
        title: 'Timestamp',
        dataIndex: 'createdAt',
        key: 'createdAt',
        render: (value) => (value ? new Date(value).toLocaleString() : '—'),
      },
    ];

    return (
      <Table
        rowKey={(record) => record._id}
        columns={columns}
        dataSource={detail.usage}
        pagination={false}
        size="small"
      />
    );
  }

  renderFinanceSummary() {
    const { detail } = this.state;
    const summary = detail && detail.finance;
    const subscriptions = detail && detail.subscriptions;
    const activeSubscription = Array.isArray(subscriptions)
      ? subscriptions.find((subscription) => String(subscription.status || '').toLowerCase() === 'active')
      : null;

    const activeQuotaLimit = activeSubscription
      && activeSubscription.quotaLimit != null
      ? Number(activeSubscription.quotaLimit)
      : null;
    const activeQuotaUsed = activeSubscription ? Number(activeSubscription.quotaUsed || 0) : 0;
    const activeQuotaRemaining =
      activeSubscription && activeSubscription.quotaRemaining != null
        ? Number(activeSubscription.quotaRemaining)
        : activeQuotaLimit != null
          ? Math.max(activeQuotaLimit - activeQuotaUsed, 0)
          : null;

    const activeCreditValue = (() => {
      if (!activeSubscription) {
        return '—';
      }
      if (activeQuotaLimit == null) {
        return 'Unlimited';
      }
      if (Number.isNaN(activeQuotaRemaining)) {
        return '—';
      }
      return `${Math.max(activeQuotaRemaining, 0).toLocaleString()} / ${Math.max(activeQuotaLimit, 0).toLocaleString()}`;
    })();

    if (!summary) {
      return <p>No financial data available.</p>;
    }

    const metrics = [
      {
        key: 'activePlan',
        label: 'Active plan',
        value: displayPlanName(summary.activePlanName) || '—',
      },
      {
        key: 'activeSubs',
        label: 'Active credit plans',
        value: summary.activeSubscriptions || 0,
      },
      {
        key: 'totalSubs',
        label: 'Total credit plans',
        value: summary.totalSubscriptions || 0,
      },
      {
        key: 'nextRenewal',
        label: 'Next renewal',
        value: summary.latestEndsAt ? new Date(summary.latestEndsAt).toLocaleDateString() : '—',
      },
      {
        key: 'currentCreditsLeft',
        label: activeSubscription ? 'Current plan credits left' : 'Credits left',
        value: activeCreditValue,
      },
      {
        key: 'currentCreditsUsed',
        label: 'Current plan credits used',
        value: activeSubscription ? activeQuotaUsed.toLocaleString() : '—',
      },
    ];

    return (
      <div className="admin-customer-finance-grid">
        {metrics.map((metric) => (
          <div key={metric.key} className="admin-customer-finance-grid__item">
            <span className="admin-customer-finance-grid__label">{metric.label}</span>
            <strong className="admin-customer-finance-grid__value">{metric.value}</strong>
          </div>
        ))}
      </div>
    );
  }

  renderUsageSummary() {
    const { detail, usagePeriod } = this.state;
    const history = Array.isArray(detail && detail.usageHistory) ? detail.usageHistory : [];
    const now = moment.utc();
    const start = usagePeriod === '30d'
      ? now.clone().startOf('day').subtract(29, 'days')
      : now.clone().startOf('month').subtract(11, 'months');
    const categories = [];
    const buckets = new Map();
    const cursor = start.clone();
    while (cursor.isSameOrBefore(now, usagePeriod === '30d' ? 'day' : 'month')) {
      const key = cursor.format(usagePeriod === '30d' ? 'YYYY-MM-DD' : 'YYYY-MM');
      categories.push(key);
      buckets.set(key, {});
      cursor.add(1, usagePeriod === '30d' ? 'day' : 'month');
    }

    const actionNames = {
      imagine: 'Image generation',
      edit: 'Image editing',
      character_create: 'Character creation',
      describe: 'Image description',
      enhance_prompt: 'Prompt enhancement',
    };
    const actionColors = {
      imagine: '#3b82f6',
      edit: '#60a5fa',
      character_create: '#8b5cf6',
      describe: '#14b8a6',
      enhance_prompt: '#f59e0b',
    };
    const totals = { actions: 0, credits: 0, untrackedActions: 0 };
    const actions = new Set();
    history.forEach((row) => {
      const key = usagePeriod === '30d' ? row.date : String(row.date || '').slice(0, 7);
      const bucket = buckets.get(key);
      if (!bucket) return;
      if (usagePeriod === '30d' && row.date < start.format('YYYY-MM-DD')) return;
      const action = row.action || 'other';
      if (!bucket[action]) bucket[action] = { credits: 0 };
      bucket[action].credits += Number(row.credits || 0);
      totals.actions += Number(row.actions || 0);
      totals.credits += Number(row.credits || 0);
      totals.untrackedActions += Number(row.untrackedActions || 0);
      actions.add(action);
    });
    const series = Array.from(actions).filter((action) => (
      categories.some((key) => (buckets.get(key)[action] || {}).credits > 0)
    )).map((action) => ({
      name: actionNames[action] || action,
      type: 'bar',
      stack: 'total',
      barMaxWidth: usagePeriod === '30d' ? 16 : 44,
      itemStyle: { color: actionColors[action] || '#94a3b8' },
      data: categories.map((key) => (buckets.get(key)[action] || {}).credits || 0),
    }));
    const chartOptions = {
      tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
      legend: { bottom: 0, type: 'scroll' },
      grid: { left: 64, right: 24, top: 24, bottom: 64, containLabel: true },
      xAxis: {
        type: 'category',
        data: categories.map((key) => moment.utc(key).format(usagePeriod === '30d' ? 'DD/MM' : 'MMM YY')),
        axisLabel: { interval: usagePeriod === '30d' ? 4 : 0 },
      },
      yAxis: { type: 'value', minInterval: 1, axisLabel: { margin: 12 } },
      series,
    };

    return (
      <div className="admin-customer-usage-summary">
        <div className="admin-customer-usage-summary__header">
          <div>
            <strong>Credits used</strong>
            <p>By activity, in UTC.</p>
          </div>
          <div className="admin-customer-usage-summary__period" role="group" aria-label="Usage period">
            <Button className={usagePeriod === '30d' ? 'is-active' : ''} onClick={() => this.setState({ usagePeriod: '30d' })}>30d</Button>
            <Button className={usagePeriod === '12m' ? 'is-active' : ''} onClick={() => this.setState({ usagePeriod: '12m' })}>12m</Button>
          </div>
        </div>
        <div className="admin-customer-usage-summary__totals">
          <span><strong>{totals.credits.toLocaleString()}</strong> credits recorded in this period</span>
        </div>
        {totals.credits > 0 ? (
          <ReactEcharts option={chartOptions} style={{ height: 340 }} notMerge />
        ) : <p className="admin-customer-usage-summary__empty">
          {totals.actions ? 'Exact credit charges are unavailable for this older activity.' : 'No credit usage recorded in this period.'}
        </p>}
        {totals.untrackedActions > 0 && (
          <p className="admin-customer-usage-summary__note">
            Some older activity has no itemized credit charge. Only verified charges appear here;
            the current plan’s total credits used is shown above.
          </p>
        )}
      </div>
    );
  }

  render() {
    const {
      loading,
      detail,
      error,
      products,
      assignModalVisible,
      assignLoading,
      assignProductId,
      assignStartDate,
      requestModalVisible,
      requestLoading,
      requestProductId,
      editProfileVisible,
      editProfileLoading,
      profileFields,
    } = this.state;
    const { admin, history, onLogout } = this.props;
    const adminUser = admin && admin.user;
    const isSuperAdmin = adminUser && adminUser.type === 'superadmin';
    const isViewer = adminUser && adminUser.type === 'viewer';
    const canDelete = !isViewer && detail && detail.canDelete;

    return (
      <AdminLayout
        title="Customer detail"
        subtitle="Account profile, credit plan history, and API usage."
        activeKey="customers"
        admin={admin}
        onLogout={onLogout}
      >
        <div className="admin-customer-detail__actions">
          <Button type="link" onClick={() => history.push('/admin/customers')}>&larr; Back to customers</Button>
          <div className="admin-customer-detail__action-group">
            {!isViewer && (
              <Button onClick={this.openRequestModal} disabled={!products.length}>
                Create payment request
              </Button>
            )}
            {isSuperAdmin && (
              <Button type="primary" onClick={this.openAssignModal} disabled={!products.length}>
                Assign credit plan
              </Button>
            )}
            {canDelete && (
              <Button
                danger
                icon={<DeleteOutlined />}
                aria-label="Remove account"
                title="Remove account"
                onClick={() => {
                  Modal.confirm({
                    title: 'Remove this customer account?',
                    content: 'This removes the account and its account records permanently.',
                    okText: 'Remove',
                    okType: 'danger',
                    cancelText: 'Cancel',
                    onOk: this.handleDelete,
                  });
                }}
              />
            )}
          </div>
        </div>
        {loading && (
          <div className="admin-customers__loading">
            <Spin />
          </div>
        )}
        {!loading && error && (
          <Card>
            <p>{error}</p>
          </Card>
        )}
        {!loading && !error && detail && (
          <div className="admin-customer-detail">
            <Card
              title="Profile"
              className="admin-customer-detail__card"
              extra={!isViewer && !detail.isSystemAccount && (
                <Button onClick={this.openEditProfile}>Edit profile</Button>
              )}
            >
              <Descriptions column={1} bordered size="small">
                <Descriptions.Item label="Email">{detail.user.email}</Descriptions.Item>
                <Descriptions.Item label="Name">{detail.user.name || '—'}</Descriptions.Item>
                <Descriptions.Item label="Company">{detail.user.company || '—'}</Descriptions.Item>
                <Descriptions.Item label="Phone">{detail.user.phone || '—'}</Descriptions.Item>
                <Descriptions.Item label="Country">{detail.user.country || '—'}</Descriptions.Item>
                <Descriptions.Item label="Roles">{detail.user.roles ? detail.user.roles.join(', ') : '—'}</Descriptions.Item>
                <Descriptions.Item label="Email verified">{detail.user.isEmailVerified ? 'Yes' : 'No'}</Descriptions.Item>
              <Descriptions.Item label="Joined">{detail.user.createdAt ? new Date(detail.user.createdAt).toLocaleString() : '—'}</Descriptions.Item>
              </Descriptions>
            </Card>
            <Card title="Plan summary" className="admin-customer-detail__card">
              {this.renderFinanceSummary()}
            </Card>
            <Card title="Credit Plans" className="admin-customer-detail__card">
              {this.renderSubscriptionTable()}
            </Card>
            <Card title="Usage summary" className="admin-customer-detail__card">
              {this.renderUsageSummary()}
            </Card>
            <Card title="Recent API usage" className="admin-customer-detail__card">
              {this.renderUsageTable()}
            </Card>
          </div>
        )}

        <Modal
          title="Edit customer profile"
          visible={editProfileVisible}
          onCancel={this.closeEditProfile}
          onOk={this.handleSaveProfile}
          confirmLoading={editProfileLoading}
          okText="Save changes"
        >
          <div className="admin-profile-form">
            {[
              ['email', 'Email'],
              ['name', 'Name'],
              ['company', 'Company'],
              ['phone', 'Phone'],
              ['country', 'Country'],
            ].map(([field, label]) => (
              <label key={field} htmlFor={`admin-profile-${field}`}>
                <span>{label}</span>
                <Input
                  id={`admin-profile-${field}`}
                  type={field === 'email' ? 'email' : 'text'}
                  maxLength={field === 'email' ? 254 : (field === 'phone' ? 40 : (field === 'country' ? 80 : 120))}
                  value={profileFields[field]}
                  onChange={(event) => this.updateProfileField(field, event.target.value)}
                  onPressEnter={this.handleSaveProfile}
                />
              </label>
            ))}
            {detail && detail.user && profileFields.email.trim().toLowerCase() !== detail.user.email && (
              <p className="admin-assign-form__hint">
                Changing the email address will require the customer to verify the new address.
              </p>
            )}
          </div>
        </Modal>

        <Modal
          title="Create payment request"
          visible={requestModalVisible}
          onCancel={this.closeRequestModal}
          onOk={this.handleCreatePaymentRequest}
          confirmLoading={requestLoading}
          okText="Create request"
        >
          <div className="admin-assign-form">
            <label htmlFor="requestProduct">Plan</label>
            <Select
              id="requestProduct"
              value={requestProductId || undefined}
              onChange={(value) => this.setState({ requestProductId: value })}
              placeholder="Select a plan"
            >
              {products.map((product) => {
                const productId = product._id || product.id;
                return (
                  <Select.Option key={productId} value={productId}>
                    {product.description} — ${Number(product.price || 0).toFixed(2)} / {this.formatProductPeriod(product)}
                  </Select.Option>
                );
              })}
            </Select>
          </div>
        </Modal>

        <Modal
          title="Assign credit plan"
          visible={assignModalVisible}
          onCancel={this.closeAssignModal}
          onOk={this.handleAssign}
          confirmLoading={assignLoading}
          okText="Assign"
        >
          <div className="admin-assign-form">
            <label htmlFor="assignProduct">Plan</label>
            <Select
              id="assignProduct"
              value={assignProductId || undefined}
              onChange={(value) => this.setState({ assignProductId: value })}
              placeholder="Select a plan"
            >
              {products.map((product) => {
                const productId = product._id || product.id;
                return (
                  <Select.Option key={productId} value={productId}>
                    {product.description} — ${Number(product.price || 0).toFixed(2)} / {this.formatProductPeriod(product)}
                  </Select.Option>
                );
              })}
            </Select>

            <label htmlFor="assignStart" className="admin-assign-form__label">Start date</label>
            <input
              id="assignStart"
              type="date"
              value={assignStartDate}
              onChange={(event) => this.setState({ assignStartDate: event.target.value })}
              max={moment().add(1, 'year').format('YYYY-MM-DD')}
            />
            <p className="admin-assign-form__hint">
              Leave blank to start the credit plan immediately.
            </p>
          </div>
        </Modal>
      </AdminLayout>
    );
  }
}

AdminCustomerDetail.propTypes = {
  admin: PropTypes.shape({
    user: PropTypes.shape({
      type: PropTypes.string,
    }),
  }),
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      userId: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  onLogout: PropTypes.func.isRequired,
};

AdminCustomerDetail.defaultProps = {
  admin: null,
};

const mapStateToProps = (state) => ({
  admin: state.auth.admin,
});

const mapDispatchToProps = {
  onLogout: clearSession,
};

export default connect(mapStateToProps, mapDispatchToProps)(AdminCustomerDetail);
